import { useMemo } from 'react';
import { Grid } from 'antd';

import { selectSiteSettings } from '@store/slices/settingsSlice.tsx';
import { selectProfile } from '@store/slices/userSlice';
import { useAppSelector } from '@store/store.tsx';
import { useDraftOverrides } from '@eshop/templates/context/DraftOverridesContext';

import { FloatingButtonsPanel } from './FloatingButtonsPanel.tsx';
import { HelpFloatButton } from './HelpFloatButton.tsx';
import { EditorFloatButton } from './EditorFloatButton.tsx';
import {
  hasWhatsappEntries,
  resolveWhatsappConfig,
} from './normalizers.ts';

export const EshopFloatButtons = () => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const eshopConfig = useAppSelector((state) => state.eshop.cfg ?? null);
  const profile = useAppSelector(selectProfile);
  const isStaff = Boolean(profile?.role?.includes('staff') || profile?.role?.includes('superuser'));
  const siteSettings = useAppSelector(selectSiteSettings);

  // Get draft state to show editor button status
  const draftOverrides = useDraftOverrides();
  const isEditorMinimized = draftOverrides?.isMinimized ?? false;
  const hasUnsavedChanges = draftOverrides?.hasUnsavedChanges ?? false;

  const { numbers: whatsappNumbers, message: resolvedMessage } = useMemo(
    () =>
      resolveWhatsappConfig({
        settingsNumbers: siteSettings?.whatsapp_numbers ?? null,
        settingsMessage: siteSettings?.whatsapp_fab_message ?? null,
        legacyNumbers: eshopConfig?.wsp_numbers ?? null,
        legacyMessage: eshopConfig?.float_btn_msj_txt ?? null,
      }),
    [
      eshopConfig?.float_btn_msj_txt,
      eshopConfig?.wsp_numbers,
      siteSettings?.whatsapp_fab_message,
      siteSettings?.whatsapp_numbers,
    ],
  );

  const floatBtnFlag = eshopConfig?.float_btn_msj;

  const fallbackEnabled = floatBtnFlag !== undefined ? Boolean(floatBtnFlag) : hasWhatsappEntries(whatsappNumbers);
  const whatsappFabEnabled = Boolean(siteSettings?.enable_whatsapp_fab ?? fallbackEnabled);
  const showWhatsapp = whatsappFabEnabled && hasWhatsappEntries(whatsappNumbers);

  const messageText = showWhatsapp ? resolvedMessage : undefined;

  // Build extra buttons array - editor button for staff, always visible
  const extraButtons = useMemo(() => {
    if (!isStaff) {
      return null;
    }
    return (
      <EditorFloatButton
        isEditorMinimized={isEditorMinimized}
        hasUnsavedChanges={hasUnsavedChanges}
      />
    );
  }, [isStaff, isEditorMinimized, hasUnsavedChanges]);

  return (
    <FloatingButtonsPanel
      whatsappNumbers={showWhatsapp ? whatsappNumbers : []}
      messageText={messageText}
      helpButton={isMobile ? undefined : <HelpFloatButton />}
      extraButtons={isMobile ? undefined : extraButtons}
    />
  );
};

export default EshopFloatButtons;
